import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    MenuItem,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import {
    Save as SaveIcon,
    RestartAlt as RebootIcon,
    Undo as UndoIcon,
} from '@mui/icons-material';
import { getFixTypeName } from '../mavlink/mavlinkHelper';
import { SectionHeader } from './Sensors';

// Per-instance parameters, expressed as two parallel naming schemes:
// - legacy: pre-ArduPilot 4.6 (GPS_TYPE, GPS_POS1_X, GPS_MB1_TYPE…)
// - modern: 4.6+ (GPS1_TYPE, GPS1_POS_X, GPS1_MB_TYPE…)
// At render time we detect which scheme the vehicle uses (whichever TYPE
// param is present) and render that full list, missing rows included so
// the layout is stable.
const INSTANCE_PARAMS = [
    {
        label: 'GNSS 1',
        schemes: [
            {
                key: 'modern',
                typeParam: 'GPS1_TYPE',
                params: [
                    'GPS1_TYPE',
                    'GPS1_GNSS_MODE',
                    'GPS1_RATE_MS',
                    'GPS1_DELAY_MS',
                    'GPS1_COM_PORT',
                    'GPS1_CAN_NODEID',
                    'GPS1_CAN_OVRIDE',
                    'GPS1_MB_TYPE',
                    'GPS1_MB_OFS_X',
                    'GPS1_MB_OFS_Y',
                    'GPS1_MB_OFS_Z',
                    'GPS1_POS_X',
                    'GPS1_POS_Y',
                    'GPS1_POS_Z',
                ],
            },
            {
                key: 'legacy',
                typeParam: 'GPS_TYPE',
                params: [
                    'GPS_TYPE',
                    'GPS_GNSS_MODE',
                    'GPS_RATE_MS',
                    'GPS_DELAY_MS',
                    'GPS_COM_PORT',
                    'GPS_CAN_NODEID1',
                    'GPS_MB1_TYPE',
                    'GPS_POS1_X',
                    'GPS_POS1_Y',
                    'GPS_POS1_Z',
                ],
            },
        ],
    },
    {
        label: 'GNSS 2',
        schemes: [
            {
                key: 'modern',
                typeParam: 'GPS2_TYPE',
                params: [
                    'GPS2_TYPE',
                    'GPS2_GNSS_MODE',
                    'GPS2_RATE_MS',
                    'GPS2_DELAY_MS',
                    'GPS2_COM_PORT',
                    'GPS2_CAN_NODEID',
                    'GPS2_CAN_OVRIDE',
                    'GPS2_MB_TYPE',
                    'GPS2_MB_OFS_X',
                    'GPS2_MB_OFS_Y',
                    'GPS2_MB_OFS_Z',
                    'GPS2_POS_X',
                    'GPS2_POS_Y',
                    'GPS2_POS_Z',
                ],
            },
            {
                key: 'legacy',
                typeParam: 'GPS_TYPE2',
                params: [
                    'GPS_TYPE2',
                    'GPS_GNSS_MODE2',
                    'GPS_RATE_MS2',
                    'GPS_DELAY_MS2',
                    'GPS_COM_PORT2',
                    'GPS_CAN_NODEID2',
                    'GPS_MB2_TYPE',
                    'GPS_POS2_X',
                    'GPS_POS2_Y',
                    'GPS_POS2_Z',
                ],
            },
        ],
    },
];

const fmt = (v, digits = 2, fallback = '—') => {
    if (v === null || v === undefined || Number.isNaN(v)) return fallback;
    return Number(v).toFixed(digits);
};

const Stat = ({ label, value, unit }) => {
    const theme = useTheme();
    return (
        <Box
            sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                px: 1.25,
                py: 0.75,
                minWidth: 120,
                flex: '1 1 120px',
                bgcolor: 'background.default',
            }}
        >
            <Typography
                sx={{
                    fontSize: 10,
                    letterSpacing: 0.6,
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                    lineHeight: 1.2,
                }}
            >
                {label}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                <Typography
                    sx={{
                        fontSize: 15,
                        fontWeight: 600,
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                        fontVariantNumeric: 'tabular-nums',
                    }}
                >
                    {value}
                </Typography>
                {unit && (
                    <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                        {unit}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

const InstanceMonitor = ({ label, data, typeParam }) => {
    const theme = useTheme();
    const typeValue = typeParam?.value;
    const disabled = typeValue === 0;
    const hasData = !!data;

    let statusChip;
    if (disabled) {
        statusChip = (
            <Chip
                size="small"
                label="Disabled"
                variant="outlined"
                sx={{ height: 20, fontSize: 11 }}
            />
        );
    } else if (!hasData) {
        statusChip = (
            <Chip
                size="small"
                label="No data"
                variant="outlined"
                sx={{ height: 20, fontSize: 11 }}
            />
        );
    } else {
        const fixOk = data.fix_type >= 3;
        statusChip = (
            <Chip
                size="small"
                color={fixOk ? 'success' : 'warning'}
                label={getFixTypeName(data.fix_type)}
                sx={{ height: 20, fontSize: 11 }}
            />
        );
    }

    const rows = [
        { label: 'Fix', value: hasData ? getFixTypeName(data.fix_type) : '—' },
        { label: 'Sats', value: hasData ? data.satellites_visible : '—' },
        { label: 'HDOP', value: hasData ? fmt(data.eph / 100, 2) : '—' },
        { label: 'VDOP', value: hasData ? fmt(data.epv / 100, 2) : '—' },
        { label: 'Speed', value: hasData ? `${fmt(data.vel / 100, 2)} m/s` : '—' },
        {
            label: 'Type',
            value:
                typeValue !== undefined && typeValue !== null
                    ? `${typeValue}`
                    : '—',
        },
    ];

    return (
        <Box
            sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                p: 1.25,
                minWidth: 220,
                flex: '1 1 240px',
                bgcolor: 'background.default',
                opacity: disabled ? 0.6 : 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
            }}
        >
            <Stack direction="row" alignItems="center" spacing={1}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, flex: 1 }}>
                    {label}
                </Typography>
                {statusChip}
            </Stack>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'auto 1fr',
                    columnGap: 1.5,
                    rowGap: 0.25,
                }}
            >
                {rows.map((r) => (
                    <React.Fragment key={r.label}>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {r.label}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                textAlign: 'right',
                                fontFamily: 'ui-monospace, monospace',
                                fontVariantNumeric: 'tabular-nums',
                            }}
                        >
                            {r.value}
                        </Typography>
                    </React.Fragment>
                ))}
            </Box>
        </Box>
    );
};

const ParameterRow = ({ name, paramEntry, pendingValue, onChange, onReset }) => {
    const theme = useTheme();
    const missing = !paramEntry;
    const meta = paramEntry?.metaData || {};
    const currentValue = paramEntry?.value;
    const value = pendingValue !== undefined ? pendingValue : currentValue;
    const dirty = pendingValue !== undefined && pendingValue !== currentValue;

    const valuesMap = meta?.values || {};
    const bitmaskMap = meta?.bitmask || {};
    const hasOptions = Object.keys(valuesMap).length > 0;
    const hasBitmask = !hasOptions && Object.keys(bitmaskMap).length > 0;
    const fields = meta?.fields || {};
    const range = fields?.Range ? fields.Range.split(' ').map(parseFloat) : null;
    const step = fields?.Increment ? parseFloat(fields.Increment) : 1;
    const unit = fields?.Units || '';

    let editor;
    if (hasOptions) {
        // Select dropdown from metadata values { humanLabel: numericValue }
        editor = (
            <Select
                size="small"
                value={value ?? ''}
                onChange={(e) => onChange(Number(e.target.value))}
                sx={{ minWidth: 220, fontSize: 13 }}
            >
                {Object.keys(valuesMap).map((label) => {
                    const numeric = Number(valuesMap[label]);
                    return (
                        <MenuItem key={label} value={numeric} sx={{ fontSize: 13 }}>
                            <span style={{ fontFamily: 'ui-monospace, monospace', minWidth: 32, display: 'inline-block' }}>
                                {numeric}
                            </span>
                            <span style={{ marginLeft: 8 }}>{label}</span>
                        </MenuItem>
                    );
                })}
                {/* Allow current value even if not in options */}
                {value !== undefined &&
                    value !== null &&
                    !Object.values(valuesMap).map(Number).includes(Number(value)) && (
                        <MenuItem value={Number(value)} sx={{ fontSize: 13, fontStyle: 'italic' }}>
                            <span style={{ fontFamily: 'ui-monospace, monospace' }}>{value}</span>
                            <span style={{ marginLeft: 8, opacity: 0.7 }}>(custom)</span>
                        </MenuItem>
                    )}
            </Select>
        );
    } else if (hasBitmask) {
        const numericValue = Number(value) || 0;
        const toggleBit = (bit) => {
            const mask = 1 << bit;
            const next = (numericValue & mask) ? (numericValue & ~mask) : (numericValue | mask);
            onChange(next);
        };
        editor = (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ rowGap: 0.5 }}>
                {Object.keys(bitmaskMap).map((label) => {
                    const bit = Number(bitmaskMap[label]);
                    const on = !!(numericValue & (1 << bit));
                    return (
                        <Chip
                            key={label}
                            size="small"
                            label={label}
                            color={on ? 'primary' : 'default'}
                            variant={on ? 'filled' : 'outlined'}
                            onClick={() => toggleBit(bit)}
                            sx={{ height: 22, fontSize: 11, cursor: 'pointer' }}
                        />
                    );
                })}
            </Stack>
        );
    } else {
        editor = (
            <TextField
                size="small"
                type="number"
                value={value ?? ''}
                onChange={(e) => onChange(Number(e.target.value))}
                inputProps={{
                    step,
                    ...(range ? { min: range[0], max: range[1] } : {}),
                    style: { fontFamily: 'ui-monospace, monospace', textAlign: 'right' },
                }}
                sx={{ width: 140 }}
            />
        );
    }

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: '180px 1fr auto',
                alignItems: 'center',
                gap: 1.5,
                py: 0.75,
                px: 1,
                borderBottom: `1px solid ${theme.palette.divider}`,
                bgcolor: dirty ? 'action.hover' : 'transparent',
            }}
        >
            <Box sx={{ minWidth: 0 }}>
                <Tooltip title={meta?.documentation || ''} arrow placement="top">
                    <Typography
                        sx={{
                            fontSize: 13,
                            fontWeight: 600,
                            fontFamily: 'ui-monospace, monospace',
                        }}
                    >
                        {name}
                    </Typography>
                </Tooltip>
                {meta?.humanName && (
                    <Typography
                        sx={{
                            fontSize: 11,
                            color: 'text.secondary',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {meta.humanName}
                    </Typography>
                )}
            </Box>
            <Stack direction="row" alignItems="center" spacing={1}>
                {missing ? (
                    <Chip
                        size="small"
                        variant="outlined"
                        label="not loaded"
                        sx={{ height: 20, fontSize: 10, opacity: 0.7 }}
                    />
                ) : (
                    editor
                )}
                {unit && !missing && (
                    <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{unit}</Typography>
                )}
                {dirty && (
                    <Chip
                        size="small"
                        label={`was ${currentValue}`}
                        sx={{ height: 18, fontSize: 10 }}
                    />
                )}
            </Stack>
            <Box>
                {dirty && (
                    <Tooltip title="Revert">
                        <Button
                            size="small"
                            startIcon={<UndoIcon sx={{ fontSize: 14 }} />}
                            onClick={onReset}
                            sx={{ minWidth: 0, px: 1 }}
                        >
                            Reset
                        </Button>
                    </Tooltip>
                )}
            </Box>
        </Box>
    );
};

const Gnss = ({ selectedMavId, mavTimestamp, paramListTimestamp }) => {
    const [gnss1, setGnss1] = useState(null);
    const [gnss2, setGnss2] = useState(null);

    const [pending, setPending] = useState({});
    const [rebootHint, setRebootHint] = useState(false);
    // local tick that also bumps on direct 'param-list' events, so we refresh
    // even if the parent throttles paramListTimestamp.
    const [localParamTick, setLocalParamTick] = useState(0);

    useEffect(() => {
        const handler = () => setLocalParamTick((t) => t + 1);
        window.addEventListener('param-list', handler);
        return () => window.removeEventListener('param-list', handler);
    }, []);

    // pull live telemetry
    useEffect(() => {
        if (!window.mav) return;
        const v = window.mav[selectedMavId];
        if (!v) return;
        if (v.GPS_RAW_INT) setGnss1(v.GPS_RAW_INT);
        if (v.GPS2_RAW) setGnss2(v.GPS2_RAW);
    }, [mavTimestamp, selectedMavId]);

    // read parameter list directly from window so we always see the latest
    const paramList = useMemo(() => {
        if (!window.paramList || !selectedMavId) return {};
        return window.paramList[selectedMavId] || {};
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedMavId, paramListTimestamp, localParamTick]);

    const handleParamChange = (name, newValue) => {
        setPending((prev) => {
            const current = paramList[name]?.value;
            const next = { ...prev };
            if (newValue === current) {
                delete next[name];
            } else {
                next[name] = newValue;
            }
            return next;
        });
    };

    const handleReset = (name) => {
        setPending((prev) => {
            const next = { ...prev };
            delete next[name];
            return next;
        });
    };

    const handleSave = () => {
        const names = Object.keys(pending);
        if (names.length === 0) return;
        const paramsToWrite = {};
        names.forEach((name) => {
            paramsToWrite[name] = {
                value: pending[name],
                type: paramList[name].type,
            };
        });
        window.api.setParams(selectedMavId, paramsToWrite);
        setPending({});
        setRebootHint(true);
    };

    const handleReboot = () => {
        window.api.reboot(selectedMavId);
        setRebootHint(false);
    };

    const dirtyCount = Object.keys(pending).length;

    return (
        <Box
            sx={{
                height: '100%',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                p: '2px',
                gap: '2px',
                boxSizing: 'border-box',
            }}
        >
            <Box sx={{ p: 2, pb: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                    <Typography variant="h6" sx={{ mr: 2 }}>GNSS</Typography>
                    {dirtyCount > 0 && (
                        <Chip
                            size="small"
                            color="warning"
                            label={`${dirtyCount} unsaved`}
                            sx={{ height: 20, fontSize: 11 }}
                        />
                    )}
                    {rebootHint && (
                        <Chip
                            size="small"
                            color="info"
                            label="Reboot required to apply"
                            sx={{ height: 20, fontSize: 11 }}
                        />
                    )}
                    <Box sx={{ flexGrow: 1 }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary', mr: 1 }}>
                        {Object.keys(paramList).length} params
                    </Typography>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                            if (window.api?.requestParamList) {
                                window.api.requestParamList(selectedMavId);
                            }
                        }}
                    >
                        Refresh
                    </Button>
                    {rebootHint && (
                        <Button
                            size="small"
                            variant="outlined"
                            color="info"
                            startIcon={<RebootIcon />}
                            onClick={handleReboot}
                        >
                            Reboot
                        </Button>
                    )}
                    <Button
                        size="small"
                        variant="contained"
                        startIcon={<SaveIcon />}
                        disabled={dirtyCount === 0}
                        onClick={handleSave}
                    >
                        Save
                    </Button>
                </Stack>
            </Box>

            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflow: 'auto',
                    px: 2,
                    pb: 2,
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    alignItems: 'flex-start',
                    gap: 2,
                }}
            >
                {INSTANCE_PARAMS.map((inst, idx) => {
                    // pick the first scheme whose TYPE param is present; fallback to first
                    const activeScheme =
                        inst.schemes.find((s) => paramList[s.typeParam] !== undefined) ||
                        inst.schemes[0];
                    const rows = activeScheme.params;
                    const data = idx === 0 ? gnss1 : gnss2;
                    const typeParam = paramList[activeScheme.typeParam];
                    return (
                        <Box key={inst.label} sx={{ flex: '1 1 420px', minWidth: 340 }}>
                            <SectionHeader
                                title={inst.label}
                                actions={
                                    <Typography
                                        variant="caption"
                                        sx={{ color: 'text.secondary' }}
                                    >
                                        {rows.length} parameter
                                        {rows.length === 1 ? '' : 's'}
                                    </Typography>
                                }
                            />
                            <Box sx={{ display: 'flex', mb: 1 }}>
                                <InstanceMonitor
                                    label={inst.label}
                                    data={data}
                                    typeParam={typeParam}
                                />
                            </Box>
                            <Box>
                                {rows.map((name) => (
                                    <ParameterRow
                                        key={name}
                                        name={name}
                                        paramEntry={paramList[name]}
                                        pendingValue={pending[name]}
                                        onChange={(v) => handleParamChange(name, v)}
                                        onReset={() => handleReset(name)}
                                    />
                                ))}
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};

export default Gnss;
